import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import type { SignInFlow } from '@/app_lib/types/accounts/signInFlow.entities';
import { CHECKOUT_REDIRECT_COOKIE_KEY, SIGNIN_REDIRECT_COOKIE_KEY } from '@/app_lib/types/accounts/signInFlow.entities';
import type { EventData } from '@/app_lib/types/Gtm';
import { getLocalStorageObject, updateLocalStorageObject } from '@/app_lib/utils/localStorageUtils';
import { clearRedirectCookie, getRedirectDestination } from '@/app_lib/utils/signinRedirect';

const assertNever = (value: never): never => {
  throw new Error(`Unhandled sign-in flow: ${String(value)}`);
};

const onTrackEventSignIn = (data: EventData): void => {
  trackEvent(data, 'signInEventTracker');
};

/**
 * Checkout: re-hydrates checkout context, emits checkoutEventTracker, persists login_method/login_type on success.
 */
const onTrackEventCheckout = (data: EventData): void => {
  const rawCheckoutTrackingProps = getLocalStorageObject('checkoutTrackingProps');
  if (data.eventAction === 'account_signin_step_1' && !rawCheckoutTrackingProps) {
    return;
  }
  const checkoutTrackingProps = (rawCheckoutTrackingProps || {}) as Record<string, string | number | boolean>;
  const loginType = data.loginType;
  let loginMethod: string | undefined;
  if (loginType === 'social') {
    loginMethod = (checkoutTrackingProps.socialType as string | undefined) ?? data.loginMethod?.toLowerCase();
  } else if (loginType === 'password' || loginType === 'new_account') {
    loginMethod = 'piano';
  }

  const checkoutParams: Record<string, string | number | boolean> = {
    ...checkoutTrackingProps,
    eventName: data.eventAction ?? '',
  };
  if (loginMethod) {
    checkoutParams.login_method = loginMethod;
  }
  if (typeof data.loginSuccess === 'boolean') {
    checkoutParams.login_success = data.loginSuccess;
  }
  if (loginType) {
    checkoutParams.login_type = loginType;
  }
  if (data.pianoUserId) {
    checkoutParams.piano_user_id = data.pianoUserId;
  }

  trackEvent({ checkoutParams: checkoutParams as Record<string, string | number> }, 'checkoutEventTracker');

  if (data.eventAction === 'login_attempt' && data.loginSuccess === true && loginMethod && loginType) {
    // biome-ignore lint/style/useNamingConvention: persisted checkout analytics field names
    updateLocalStorageObject('checkoutTrackingProps', { login_method: loginMethod, login_type: loginType });
  }
};

interface RedirectOnAuthOptions {
  flow: SignInFlow;
  redirectCookieKey: string;
  refreshUserAccess: () => Promise<void>;
  router: { refresh(): void };
}

export const getRedirectCookieKey = (flow: SignInFlow): string => {
  switch (flow) {
    case 'checkout':
      return CHECKOUT_REDIRECT_COOKIE_KEY;
    case 'standalone':
      return SIGNIN_REDIRECT_COOKIE_KEY;
    default:
      return assertNever(flow);
  }
};

export const getOnTrackEvent = (flow: SignInFlow): { track: (data: EventData) => void } => {
  switch (flow) {
    case 'checkout':
      return { track: onTrackEventCheckout };
    case 'standalone':
      return { track: onTrackEventSignIn };
    default:
      return assertNever(flow);
  }
};

export const redirectOnAuth = async ({
  flow,
  redirectCookieKey,
  refreshUserAccess,
  router,
}: RedirectOnAuthOptions): Promise<void> => {
  if (flow === 'standalone') {
    const redirect = getRedirectDestination(redirectCookieKey);
    clearRedirectCookie(redirectCookieKey);
    window.location.href = redirect;
    return;
  }
  if (flow === 'checkout') {
    await refreshUserAccess();
    router.refresh();
    return;
  }
};
